import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Repeat } from 'lucide-react';
import { calendarDay, dueDeadline, dueState, formatDue, type MyCard } from '@kaba/shared';
import { api } from '../lib/api';
import { navigate } from '../lib/router';
import { cx } from './ui/ui';

type Bucket = 'overdue' | 'today' | 'week' | 'later' | 'none';
const BUCKETS: [Bucket, string][] = [
  ['overdue', 'Überfällig'],
  ['today', 'Heute'],
  ['week', 'Nächste 7 Tage'],
  ['later', 'Später'],
  ['none', 'Ohne Frist'],
];

function bucketOf(c: MyCard): Bucket {
  if (!c.dueAt) return 'none';
  if (dueState(c.dueAt, c.dueHasTime, null) === 'overdue') return 'overdue';
  const due = calendarDay(c.dueAt, c.dueHasTime);
  const now = new Date();
  const endToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  if (due <= endToday) return 'today';
  if (due.getTime() - endToday.getTime() <= 7 * 86400_000) return 'week';
  return 'later';
}

/** Alle mir zugewiesenen offenen Karten über alle Boards, nach Dringlichkeit */
export function MyCardsList() {
  const { data, isLoading } = useQuery({ queryKey: ['my-cards'], queryFn: () => api.get<MyCard[]>('/api/me/cards'), staleTime: 15_000 });
  const groups = useMemo(() => {
    const open = (data ?? []).filter((c) => !c.completedAt);
    const m = new Map<Bucket, MyCard[]>();
    for (const c of open) m.set(bucketOf(c), [...(m.get(bucketOf(c)) ?? []), c]);
    const key = (c: MyCard) => (c.dueAt ? dueDeadline(c.dueAt, c.dueHasTime).getTime() : Infinity);
    for (const list of m.values()) list.sort((a, b) => key(a) - key(b));
    return m;
  }, [data]);

  if (isLoading) return <p className="text-sm text-[var(--ink-soft)]">Lade …</p>;
  if (!data?.some((c) => !c.completedAt))
    return (
      <div className="rounded-2xl border-2 border-dashed border-[var(--line)] p-8 text-center text-sm text-[var(--ink-soft)]">
        Dir ist gerade keine offene Karte zugewiesen.
      </div>
    );

  return (
    <div className="flex flex-col gap-5 pb-10">
      {BUCKETS.filter(([b]) => groups.get(b)?.length).map(([b, label]) => (
        <section key={b} className="flex flex-col gap-2">
          <h3 className={cx('text-sm font-semibold', b === 'overdue' && 'text-rose-600')}>
            {label} <span className="font-normal text-[var(--ink-soft)]">({groups.get(b)!.length})</span>
          </h3>
          <ul className="flex flex-col divide-y divide-[var(--line)] overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--panel)]">
            {groups.get(b)!.map((c) => (
              <li key={c.id}>
                <a
                  href={`/b/${c.boardId}?card=${c.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/b/${c.boardId}?card=${c.id}`);
                  }}
                  className="flex min-h-14 items-center gap-3 px-3 py-2 hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] sm:text-sm">{c.title}</span>
                    <span className="text-xs text-[var(--ink-soft)]">
                      {c.boardTitle} · {c.columnTitle}
                      {c.blocked && <span className="ml-1 rounded bg-rose-600 px-1 text-white">Blockiert</span>}
                    </span>
                  </span>
                  {c.recurrence && <Repeat size={14} className="shrink-0 text-[var(--ink-soft)]" aria-label="Wiederkehrend" />}
                  {c.dueAt && (
                    <span className={cx('inline-flex shrink-0 items-center gap-1 rounded px-1.5 text-xs', b === 'overdue' ? 'bg-rose-600 text-white' : b === 'today' ? 'bg-amber-400 text-amber-950' : 'text-[var(--ink-soft)]')}>
                      <Clock size={12} /> {formatDue(c.dueAt, c.dueHasTime)}
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
